
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSave, MdFormatListBulleted, MdPerson, MdAdd, MdDelete } from 'react-icons/md';
import './AddNumber.css';
import Swal from 'sweetalert2';
import axios from 'axios';
import { CREATE_NUMBER_API, BULK_CREATE_NUMBERS_API } from '../config/API';
import Loader from '../components/common/Loader';
import { showErrorToast } from '../utils/toastUtils';

const AddNumber = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState('single'); // 'single' or 'bulk'
    const [formData, setFormData] = useState({
        number: '',
        name: '',
        bulkNumbers: [{ number: '', name: '' }] // Changed to array for bulk
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleBulkChange = (index, e) => {
        const { name, value } = e.target;
        const updatedBulk = [...formData.bulkNumbers];
        updatedBulk[index][name] = value;
        setFormData(prev => ({
            ...prev,
            bulkNumbers: updatedBulk
        }));
    };

    const addBulkRow = () => {
        setFormData(prev => ({
            ...prev,
            bulkNumbers: [...prev.bulkNumbers, { number: '', name: '' }]
        }));
    };

    const removeBulkRow = (index) => {
        if (formData.bulkNumbers.length === 1) {
            setFormData(prev => ({
                ...prev,
                bulkNumbers: [{ number: '', name: '' }]
            }));
            return;
        }
        const updatedBulk = formData.bulkNumbers.filter((_, i) => i !== index);
        setFormData(prev => ({
            ...prev,
            bulkNumbers: updatedBulk
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const title = mode === 'single' ? 'Save Number?' : 'Save Multiple Numbers?';
        const text = mode === 'single' ? 'Do you want to save this number?' : `Do you want to save ${formData.bulkNumbers.filter(b => b.number).length} numbers?`;

        const result = await Swal.fire({
            title: title,
            text: text,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Save',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');

                let response;
                if (mode === 'single') {
                    if (!formData.number) {
                        showErrorToast('Please enter a number');
                        setLoading(false);
                        return;
                    }
                    response = await axios.post(CREATE_NUMBER_API, {
                        number: formData.number,
                        name: formData.name
                    }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                } else {
                    const validNumbers = formData.bulkNumbers.filter(item => item.number.trim().length > 0);

                    if (validNumbers.length === 0) {
                        showErrorToast('Please enter at least one valid number');
                        setLoading(false);
                        return;
                    }

                    response = await axios.post(BULK_CREATE_NUMBERS_API, { numbers: validNumbers }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                }

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Saved!',
                        text: response.data.message || 'Numbers created successfully',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    navigate('/settings');
                } else {
                    showErrorToast(response.data.error || response.data.message || 'Failed to create');
                }
            } catch (error) {
                console.error('Error creating:', error);
                const errorData = error.response?.data?.error || error.response?.data?.message || 'An error occurred';
                showErrorToast(errorData);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return <Loader fullPage text="Creating..." />;
    }

    return (
        <div className="add-product-page">
            <div className="page-header">
                <button onClick={() => navigate('/settings')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>{mode === 'single' ? 'Add New Number' : 'Bulk Add Numbers'}</h2>
            </div>

            <div className="tabs-container">
                <button
                    className={`nav-tab ${mode === 'single' ? 'active' : ''}`}
                    onClick={() => setMode('single')}
                >
                    <MdPerson /> Single
                </button>
                <button
                    className={`nav-tab ${mode === 'bulk' ? 'active' : ''}`}
                    onClick={() => setMode('bulk')}
                >
                    <MdFormatListBulleted /> Bulk
                </button>
            </div>

            <form onSubmit={handleSubmit} className="product-form">
                <div className="form-section">
                    <h3>{mode === 'single' ? 'Number Information' : 'Enter Multiple Numbers'}</h3>
                    {mode === 'single' ? (
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="e.g. John Doe"
                                />
                            </div>
                            <div className="form-group">
                                <label>Mobile Number <span className="required-star">*</span></label>
                                <input
                                    type="text"
                                    name="number"
                                    value={formData.number}
                                    onChange={handleChange}
                                    placeholder="e.g. 9876543210"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="bulk-rows-container">
                            <div className="bulk-rows-header">
                                <div className="header-name">Name</div>
                                <div className="header-number">Mobile Number</div>
                                <div className="header-action"></div>
                            </div>
                            {formData.bulkNumbers.map((row, index) => (
                                <div key={index} className="bulk-row">
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="name"
                                            value={row.name}
                                            onChange={(e) => handleBulkChange(index, e)}
                                            placeholder="Contact Name"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="number"
                                            value={row.number}
                                            onChange={(e) => handleBulkChange(index, e)}
                                            placeholder="9876543210"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="remove-row-btn"
                                        onClick={() => removeBulkRow(index)}
                                        title="Remove Row"
                                    >
                                        <MdDelete />
                                    </button>
                                </div>
                            ))}
                            <button type="button" className="add-row-btn" onClick={addBulkRow}>
                                <MdAdd /> Add Another Row
                            </button>
                        </div>
                    )}
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/settings')} className="cancel-btn">
                        Cancel
                    </button>
                    <button type="submit" className="save-btn">
                        <MdSave /> Save {mode === 'single' ? 'Number' : 'Numbers'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddNumber;
